import React, { useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { getCroppedBlob } from "../utils/cropImage";

export default function ImageField({
  label,
  name,
  value,
  disabled,
  onChange,
  aspect = 4 / 3, // change to 1 for square avatars
  mimeType = "image/jpeg",
  helpText,
}) {
  const inputRef = useRef(null);

  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const isLogo = name === "logo";
  const minZoom = isLogo ? 0.25 : 1;

  // --- value can be: "", string, object, array ---
  const isObj = value && typeof value === "object" && !Array.isArray(value);

  // Local (picked) previews (only exist when user picked a file)
  const previewUrl = isObj ? value.previewUrl || "" : "";
  const croppedPreviewUrl = isObj ? value.croppedPreviewUrl || "" : "";

  // Normalize persisted images into a list for thumbnails/display
  const persistedList = useMemo(() => {
    if (!value) return [];

    // string url
    if (typeof value === "string") return [value];

    // array of strings/objects
    if (Array.isArray(value)) return value.filter(Boolean);

    // object (ImageKit-ish or similar)
    const u = value.thumbnailUrl || value.url || value.imageUrl || "";
    return u ? [value] : [];
  }, [value]);

  const getPersistedThumb = (v) => {
    if (!v) return "";
    if (typeof v === "string") return v;
    return v.thumbnailUrl || v.url || v.imageUrl || "";
  };

  const getPersistedFull = (v) => {
    if (!v) return "";
    if (typeof v === "string") return v;
    return v.url || v.imageUrl || v.thumbnailUrl || "";
  };

  // Primary persisted image (first item)
  const persistedPrimary = persistedList[0] || null;
  const persistedUrl = getPersistedThumb(persistedPrimary);

  // What we show as the big preview
  const displayUrl = croppedPreviewUrl || previewUrl || persistedUrl;

  const commonLabel = useMemo(
    () => (
      <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
        {label || name}
      </label>
    ),
    [label, name]
  );

  function revoke(url) {
    try {
      if (url) URL.revokeObjectURL(url);
    } catch {}
  }

  function pickFile() {
    inputRef.current?.click();
  }

  function remove() {
    // Only removes local picked file + previews (does not delete persisted)
    revoke(isObj ? value.previewUrl : "");
    revoke(isObj ? value.croppedPreviewUrl : "");
    onChange("");
  }

  function onFileChange(e) {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) return;

    // cleanup previous local urls if any
    revoke(isObj ? value.previewUrl : "");
    revoke(isObj ? value.croppedPreviewUrl : "");

    const url = URL.createObjectURL(file);

    // store original + open cropper
    onChange({
      file,
      previewUrl: url,
      croppedPreviewUrl: "",
      cropPixels: null,
    });

    setCrop({ x: 0, y: 0 });
    setZoom(isLogo ? 0.75 : 1);
    setCroppedAreaPixels(null);
    setCropOpen(true);
  }

  function onCropComplete(_area, areaPixels) {
    setCroppedAreaPixels(areaPixels);
  }

  async function confirmCrop() {
    if (!previewUrl || !croppedAreaPixels) return;

    try {
      setBusy(true);

      // Create cropped blob + preview (client-side)
      const blob = await getCroppedBlob(previewUrl, croppedAreaPixels, {
        mimeType,
        quality: 0.92,
      });

      // replace old cropped preview url if exists
      revoke(isObj ? value.croppedPreviewUrl : "");

      const croppedUrl = URL.createObjectURL(blob);

      // Keep original file for upload, store cropPixels for later
      onChange({
        ...(isObj ? value : {}),
        croppedPreviewUrl: croppedUrl,
        cropPixels: croppedAreaPixels,
        cropMimeType: mimeType,
        // If you later want to upload the CROPPED blob instead, store it:
        // croppedBlob: blob,
      });

      setCropOpen(false);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Crop failed");
    } finally {
      setBusy(false);
    }
  }

  const hasPickedFile = !!(isObj && value.file);

  return (
    <div style={{ marginBottom: 14 }}>
      {commonLabel}
      {helpText ? (
        <div style={{ margin: "-2px 0 8px", color: "#555", fontSize: 13 }}>{helpText}</div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        disabled={disabled}
        style={{ display: "none" }}
        onChange={onFileChange}
      />

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button
          type="button"
          disabled={disabled}
          onClick={pickFile}
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px solid #ccc",
            background: disabled ? "#f7f7f7" : "#fff",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          {hasPickedFile ? "Change Image" : "Upload Image"}
        </button>

        {hasPickedFile ? (
          <>
            <button
              type="button"
              disabled={disabled}
              onClick={() => setCropOpen(true)}
              style={{
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid #ccc",
                background: disabled ? "#f7f7f7" : "#fff",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              Crop
            </button>

            <button
              type="button"
              disabled={disabled}
              onClick={remove}
              style={{
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid #ccc",
                background: disabled ? "#f7f7f7" : "#fff",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              Remove
            </button>
          </>
        ) : null}
      </div>

      {displayUrl ? (
        <div style={{ marginTop: 10 }}>
          <img
            src={displayUrl}
            alt=""
            style={{
              width: "100%",
              maxWidth: 520,
              borderRadius: 10,
              border: "1px solid #e5e5e5",
              display: "block",
              objectFit: isLogo ? "contain" : "cover",
              aspectRatio: isLogo ? "1 / 1" : undefined,
              background: isLogo ? "#fff" : undefined,
            }}
          />

          {/* Thumbnail strip when persisted has multiple images */}
          {persistedList.length > 1 ? (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {persistedList.map((img, idx) => {
                const thumb = getPersistedThumb(img);
                const full = getPersistedFull(img);

                return (
                  <a
                    key={full || idx}
                    href={full}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "block" }}
                  >
                    <img
                      src={thumb}
                      alt=""
                      style={{
                        width: 96,
                        height: 64,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #e5e5e5",
                        display: "block",
                      }}
                    />
                  </a>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* CROP MODAL (only when we have a local picked image) */}
      {cropOpen && previewUrl ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => !busy && setCropOpen(false)}
        >
          <div
            style={{
              width: "min(900px, 96vw)",
              height: "min(650px, 90vh)",
              background: "#111",
              borderRadius: 12,
              overflow: "hidden",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ position: "relative", width: "100%", height: "85%", background: "#f3f4f6" }}>
              <Cropper
                image={previewUrl}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                minZoom={minZoom}
                maxZoom={3}
                objectFit="contain"
                restrictPosition={!isLogo}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div
              style={{
                height: "15%",
                background: "#fff",
                display: "flex",
                gap: 12,
                alignItems: "center",
                padding: 12,
              }}
            >
              <label style={{ display: "flex", gap: 8, alignItems: "center", flex: 1 }}>
                <span style={{ minWidth: 48 }}>Zoom</span>
                <input
                  type="range"
                  min={minZoom}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ width: "100%" }}
                  disabled={busy}
                />
              </label>

              <button type="button" disabled={busy} onClick={() => setCropOpen(false)}>
                Cancel
              </button>

              <button type="button" disabled={busy || !croppedAreaPixels} onClick={confirmCrop}>
                {busy ? "Working..." : "Use Crop"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
